import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Send, Trash2, Wallet, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { openInNewTab } from '@/lib/utils';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { PAYMENT_METHOD_LABELS, dayFr, gnf, invoiceBadge } from '@/lib/invoices';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { CustomerInvoiceDetail, CustomerInvoiceLine, CustomerPayment } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/invoices/$id')({ component: InvoiceDetailPage });

function InvoiceDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [paying, setPaying] = useState(false);
  const canWrite = useHasPermission('invoices.write');

  const { data: inv, isLoading } = useQuery({
    queryKey: ['customer-invoice', id],
    queryFn: async () => (await api.get<CustomerInvoiceDetail>(`/customer-invoices/${id}`)).data,
  });

  const refresh = (next?: CustomerInvoiceDetail) => {
    if (next) qc.setQueryData(['customer-invoice', id], next);
    void qc.invalidateQueries({ queryKey: ['customer-invoices'] });
  };

  const issue = useMutation({
    mutationFn: async () => (await api.post<CustomerInvoiceDetail>(`/customer-invoices/${id}/issue`)).data,
    onSuccess: (next) => { refresh(next); toast.success(`Facture ${next.number} émise`); },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Émission impossible')),
  });

  const cancel = useMutation({
    mutationFn: async () => (await api.post<CustomerInvoiceDetail | { deleted: true }>(`/customer-invoices/${id}/cancel`)).data,
    onSuccess: (res) => {
      if ('deleted' in res) {
        refresh();
        toast.success('Brouillon supprimé');
        void nav({ to: '/invoices' });
      } else {
        refresh(res);
        toast.success('Facture annulée');
      }
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Annulation impossible')),
  });

  const download = async () => {
    try {
      await openInNewTab(async () => (await api.get<{ url: string }>(`/customer-invoices/${id}/pdf`)).data.url);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'PDF indisponible'));
    }
  };

  if (isLoading) return <div className="p-6"><Skeleton className="h-64 w-full" /></div>;
  if (!inv) return <div className="p-6 text-muted-foreground">Facture introuvable</div>;

  const draft = inv.status === 'DRAFT';
  // Lignes modifiables : brouillon et droit d'écriture
  const editable = draft && canWrite;
  const remaining = inv.total - inv.amountPaid;
  const badge = invoiceBadge(inv);

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/invoices"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold font-mono">{inv.number ?? 'Brouillon'}</h1>
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <div className="ml-auto flex gap-2">
          {editable && (
            <>
              <Button
                variant="outline"
                onClick={() => { if (window.confirm('Supprimer ce brouillon ? Les missions redeviennent facturables.')) cancel.mutate(); }}
                disabled={cancel.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Supprimer
              </Button>
              <Button
                onClick={() => { if (window.confirm('Émettre la facture ? Elle ne sera plus modifiable.')) issue.mutate(); }}
                disabled={issue.isPending || inv.lines.length === 0}
              >
                <Send className="h-4 w-4 mr-1" /> Émettre
              </Button>
            </>
          )}
          {!draft && (
            <Button variant="outline" onClick={() => void download()}>
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
          )}
          {canWrite && inv.status === 'ISSUED' && (
            <Button
              variant="outline"
              onClick={() => { if (window.confirm('Annuler cette facture ? Ses missions redeviendront facturables.')) cancel.mutate(); }}
              disabled={cancel.isPending}
            >
              <XCircle className="h-4 w-4 mr-1" /> Annuler
            </Button>
          )}
          {canWrite && (inv.status === 'ISSUED' || inv.status === 'PARTIALLY_PAID') && (
            <Button onClick={() => setPaying(true)}><Wallet className="h-4 w-4 mr-1" /> Enregistrer un paiement</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 text-sm">
        <Card><CardContent className="p-4 space-y-1">
          <p className="text-muted-foreground text-xs">Client</p>
          <Link to="/clients/$id" params={{ id: inv.clientId }} className="font-semibold text-primary-500 hover:underline">{inv.client.name}</Link>
        </CardContent></Card>
        <Card><CardContent className="p-4 space-y-1">
          <p className="text-muted-foreground text-xs">Période</p>
          <p className="font-semibold">{dayFr(inv.periodStart)} – {dayFr(inv.periodEnd)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 space-y-1">
          <p className="text-muted-foreground text-xs">Émise le / échéance</p>
          <p className={`font-semibold ${inv.overdue ? 'text-danger' : ''}`}>{dayFr(inv.issueDate)} / {dayFr(inv.dueDate)}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Détail{editable && <span className="text-sm font-normal text-muted-foreground ml-2">— quantités et prix modifiables avant émission</span>}</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Désignation</TableHead>
                <TableHead className="text-right w-36">Quantité</TableHead>
                <TableHead className="text-right w-40">Prix unitaire HT</TableHead>
                <TableHead className="text-right w-40">Montant HT</TableHead>
                {editable && <TableHead className="w-10" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {inv.lines.map((l) => (
                <LineRow key={l.id} invoiceId={id} line={l} editable={editable} currency={inv.currency} onChange={refresh} />
              ))}
              {inv.lines.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-muted-foreground">Aucune ligne</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex justify-end mt-4">
            <dl className="w-72 space-y-1 text-sm">
              <Row label="Total HT" value={gnf(inv.subtotal, inv.currency)} />
              <Row label={`TVA ${inv.vatRate} %`} value={gnf(inv.vatAmount, inv.currency)} />
              <Row label="Total TTC" value={gnf(inv.total, inv.currency)} strong />
              {inv.amountPaid > 0 && <Row label="Déjà réglé" value={gnf(inv.amountPaid, inv.currency)} />}
              {!draft && inv.status !== 'CANCELLED' && <Row label="Reste à payer" value={gnf(remaining, inv.currency)} strong />}
            </dl>
          </div>
        </CardContent>
      </Card>

      {inv.payments.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Encaissements</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Moyen</TableHead>
                  <TableHead>Référence</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inv.payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{dayFr(p.paidAt)}</TableCell>
                    <TableCell>{PAYMENT_METHOD_LABELS[p.method]}</TableCell>
                    <TableCell>{p.reference ?? '—'}</TableCell>
                    <TableCell className="text-right">{gnf(p.amount, inv.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <PaymentDialog open={paying} onOpenChange={setPaying} invoiceId={id} remaining={remaining} currency={inv.currency} onDone={refresh} />
    </div>
  );
}

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className={`flex justify-between ${strong ? 'font-bold text-base' : ''}`}>
    <dt>{label}</dt><dd>{value}</dd>
  </div>
);

/** Ligne de facture ; en brouillon, quantité et prix modifiables (enregistrés à la sortie du champ). */
function LineRow({ invoiceId, line, editable, currency, onChange }: {
  invoiceId: string; line: CustomerInvoiceLine; editable: boolean; currency: string;
  onChange: (next: CustomerInvoiceDetail) => void;
}) {
  const [qty, setQty] = useState(String(line.quantity));
  const [price, setPrice] = useState(String(line.unitPrice));

  const save = useMutation({
    mutationFn: async (body: { quantity?: number; unitPrice?: number }) =>
      (await api.patch<CustomerInvoiceDetail>(`/customer-invoices/${invoiceId}/lines/${line.id}`, body)).data,
    onSuccess: onChange,
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Modification refusée')),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete<CustomerInvoiceDetail>(`/customer-invoices/${invoiceId}/lines/${line.id}`)).data,
    onSuccess: onChange,
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Suppression refusée')),
  });

  const commit = (field: 'quantity' | 'unitPrice', raw: string, current: number) => {
    const v = Number(raw.replace(',', '.'));
    if (raw.trim() === '' || Number.isNaN(v) || v < 0) {
      toast.error('Valeur invalide');
      if (field === 'quantity') setQty(String(line.quantity)); else setPrice(String(line.unitPrice));
      return;
    }
    if (v !== current) save.mutate({ [field]: v });
  };

  return (
    <TableRow>
      <TableCell>
        {line.description}
        {line.mission && (
          <Link to="/missions/$id" params={{ id: line.mission.id }} className="ml-2 text-xs text-primary-500 hover:underline">voir la mission</Link>
        )}
      </TableCell>
      <TableCell className="text-right">
        {editable ? (
          <div className="flex items-center justify-end gap-1">
            <Input className="h-8 w-24 text-right" inputMode="decimal" value={qty}
              onChange={(e) => setQty(e.target.value)} onBlur={() => commit('quantity', qty, line.quantity)} />
            <span className="text-xs text-muted-foreground">{line.unit}</span>
          </div>
        ) : `${line.quantity.toLocaleString('fr-FR')} ${line.unit}`}
      </TableCell>
      <TableCell className="text-right">
        {editable ? (
          <Input className="h-8 w-32 ml-auto text-right" inputMode="numeric" value={price}
            onChange={(e) => setPrice(e.target.value)} onBlur={() => commit('unitPrice', price, line.unitPrice)} />
        ) : gnf(line.unitPrice, currency)}
      </TableCell>
      <TableCell className="text-right font-medium">{gnf(line.amount, currency)}</TableCell>
      {editable && (
        <TableCell>
          <Button size="sm" variant="ghost" aria-label="Retirer la ligne" onClick={() => { if (window.confirm('Supprimer cette ligne ?')) remove.mutate(); }} disabled={remove.isPending}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </TableCell>
      )}
    </TableRow>
  );
}

function PaymentDialog({ open, onOpenChange, invoiceId, remaining, currency, onDone }: {
  open: boolean; onOpenChange: (o: boolean) => void; invoiceId: string; remaining: number; currency: string;
  onDone: (next: CustomerInvoiceDetail) => void;
}) {
  const today = new Date();
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`,
  );
  const [method, setMethod] = useState<CustomerPayment['method']>('BANK_TRANSFER');
  const [reference, setReference] = useState('');

  const pay = useMutation({
    mutationFn: async () =>
      (await api.post<CustomerInvoiceDetail>(`/customer-invoices/${invoiceId}/payments`, {
        amount: Number(amount), paidAt, method, reference: reference.trim() || undefined,
      })).data,
    onSuccess: (next) => {
      onDone(next);
      toast.success(next.status === 'PAID' ? 'Facture soldée' : 'Paiement enregistré');
      onOpenChange(false);
      setAmount(''); setReference('');
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Paiement refusé')),
  });

  const valid = Number(amount) > 0 && Number(amount) <= remaining + 0.5 && !!paidAt;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Enregistrer un paiement</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="pay-amount">Montant ({currency})</Label>
            <div className="flex gap-2">
              <Input id="pay-amount" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Button type="button" variant="outline" onClick={() => setAmount(String(Math.round(remaining)))}>Solde</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Reste à payer : {gnf(remaining, currency)}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pay-date">Date</Label>
              <Input id="pay-date" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
            </div>
            <div>
              <Label>Moyen</Label>
              <Select value={method} onValueChange={(v) => setMethod(v as CustomerPayment['method'])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PAYMENT_METHOD_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="pay-ref">Référence (n° de virement, transaction mobile money…)</Label>
            <Input id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={() => pay.mutate()} disabled={!valid || pay.isPending}>
            {pay.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
